<template>
  <div :class="['textbook-page', { 'is-eye-care': eyeCareMode }]">
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载教材...</p>
    </div>

    <div v-else-if="errorMessage" class="error-container">
      <p>{{ errorMessage }}</p>
      <button class="btn btn--primary" @click="$router.back()">返回</button>
    </div>

    <div v-else class="textbook-container">
      <div class="textbook-sidebar">
        <div class="textbook-header">
          <h2>{{ textbook.title }}</h2>
          <button class="btn-close" @click="$router.back()">×</button>
        </div>

        <div class="textbook-info">
          <p><strong>章节：</strong>{{ textbook.chapter_title || '-' }}</p>
          <p v-if="textbook.read_mode_word"><strong>阅读模式：</strong>{{ textbook.read_mode_word }}</p>
          <p v-if="textbook.file_type"><strong>文件类型：</strong>{{ textbook.file_type }}</p>
          <p v-if="textbook.total_pages"><strong>页数：</strong>{{ textbook.total_pages }} 页</p>
        </div>

        <div v-if="toc && toc.length > 0" class="textbook-toc">
          <h3>目录</h3>
          <div class="toc-list">
            <div
              v-for="(item, index) in toc"
              :key="index"
              :class="['toc-item', { 'is-active': currentPage === tocPage(item) }]"
              @click="goToPage(tocPage(item))"
            >
              <span class="toc-title">{{ item.title }}</span>
              <span class="toc-page">P{{ tocPage(item) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="textbook-viewer">
        <ControlledPdfReader
          v-if="isPdfFile && previewUrl && requiresControlledReader"
          ref="controlledReader"
          :textbook-id="textbookId"
          :file-url="previewUrl"
          @progress-change="handleControlledProgress"
        />

        <template v-else>
          <div v-if="isPdfFile && previewUrl && canOpenOriginal" class="viewer-toolbar">
            <div class="toolbar-left">
              <button class="btn-icon" :disabled="currentPage <= 1" @click="prevPage">‹ 上一页</button>
              <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
              <button class="btn-icon" :disabled="currentPage >= totalPages" @click="nextPage">下一页 ›</button>
            </div>
            <div class="toolbar-right">
              <button
                :class="['btn-icon', 'btn-eye-care', { 'is-active': eyeCareMode }]"
                type="button"
                :aria-pressed="String(eyeCareMode)"
                @click="toggleEyeCareMode"
              >
                {{ eyeCareMode ? '关闭护眼' : '护眼模式' }}
              </button>
              <button class="btn-icon" :disabled="zoom <= 0.5" @click="zoomOut">-</button>
              <span class="zoom-indicator">{{ Math.round(zoom * 100) }}%</span>
              <button class="btn-icon" :disabled="zoom >= 2" @click="zoomIn">+</button>
              <button class="btn-icon" @click="resetZoom">重置</button>
            </div>
          </div>

          <div class="viewer-content">
            <iframe
              v-if="previewUrl && canOpenOriginal"
              :src="standardPreviewUrl"
              class="textbook-iframe"
              frameborder="0"
            ></iframe>
            <div v-else class="no-preview">
              <p v-if="requiresControlledReader">该教材必须使用受控阅读器，当前文件类型暂不支持直接打开。</p>
              <p v-else>暂无预览</p>
              <a
                v-if="textbook.file_url && canOpenOriginal"
                :href="textbook.file_url"
                target="_blank"
                rel="noopener"
                class="btn btn--primary"
              >
                打开教材
              </a>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import ControlledPdfReader from '@/components/textbook/ControlledPdfReader.vue'
import { fetchTextbookDetail, fetchTextbookPreviewConfig } from '@/api/courseTextbook'
import { reportLearningActivity, ACTIVITY_MATERIAL_READ, ACTIVITY_PAGE_VIEW } from '@/api/learningActivity'
import { sessionState } from '@/store/session'
import { resolveAssetUrl } from '@/utils/format'
import { isPptFile } from '@/utils/pptPreview'

export default {
  name: 'Textbook',
  components: {
    ControlledPdfReader
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      textbook: {},
      previewConfig: {},
      toc: [],
      currentPage: 1,
      totalPages: 0,
      zoom: 1,
      previewUrl: '',
      eyeCareMode: false
    }
  },
  computed: {
    textbookId() {
      return this.$route.params.id || this.$route.query.id
    },
    chapterId() {
      return this.$route.query.chapter_id
    },
    normalizedFileType() {
      const type = this.previewConfig.file_type || this.textbook.file_type || ''
      if (type) return String(type).toLowerCase().replace(/^\./, '')
      const url = this.previewUrl.split('?')[0]
      const match = url.match(/\.([a-z0-9]+)$/i)
      return match ? match[1].toLowerCase() : ''
    },
    isPdfFile() {
      return this.normalizedFileType === 'pdf'
    },
    standardPreviewUrl() {
      if (!this.previewUrl || !this.isPdfFile) return this.previewUrl
      const sourceUrl = this.previewUrl.split('#')[0]
      return `${sourceUrl}#page=${this.currentPage}&zoom=${Math.round(this.zoom * 100)}`
    },
    requiresControlledReader() {
      return Number(this.previewConfig.requires_controlled_reader) === 1
    },
    canOpenOriginal() {
      return Number(this.previewConfig.can_open_original) !== 0
    }
  },
  created() {
    this.eyeCareMode = window.localStorage.getItem('textbook-eye-care-mode') === '1'
    this.loadTextbook()
  },
  methods: {
    toggleEyeCareMode() {
      this.eyeCareMode = !this.eyeCareMode
      window.localStorage.setItem('textbook-eye-care-mode', this.eyeCareMode ? '1' : '0')
    },

    async loadTextbook() {
      if (!this.textbookId) {
        this.errorMessage = '缺少教材ID'
        this.loading = false
        return
      }

      try {
        this.loading = true
        this.errorMessage = ''

        this.textbook = await fetchTextbookDetail(this.textbookId)

        if (this.textbook.toc) {
          try {
            this.toc = typeof this.textbook.toc === 'string'
              ? JSON.parse(this.textbook.toc)
              : this.textbook.toc
          } catch (e) {
            this.toc = []
          }
        }

        this.totalPages = this.textbook.total_pages || 0

        await this.loadPreviewConfig()
        this.reportTextbookActivity(ACTIVITY_PAGE_VIEW, '电子教材页面')
        this.reportTextbookActivity(ACTIVITY_MATERIAL_READ, '阅读电子教材')
      } catch (error) {
        this.errorMessage = error.message || '加载教材失败'
      } finally {
        this.loading = false
      }
    },

    reportTextbookActivity(activityType, pageTitle) {
      if (!sessionState.token || !this.textbook.id) return
      reportLearningActivity({
        activity_type: activityType,
        course_id: Number(this.textbook.course_id || this.$route.query.course_id) || 0,
        chapter_id: Number(this.textbook.chapter_id || this.chapterId) || 0,
        resource_type: 'textbook',
        resource_id: Number(this.textbook.id || this.textbookId) || 0,
        resource_title: this.textbook.title || '',
        page_route: this.$route.path,
        page_title: pageTitle,
        page_params: this.$route.query
      }).catch(error => console.error('上报教材学习日志失败', error))
    },

    async loadPreviewConfig() {
      try {
        this.previewConfig = await fetchTextbookPreviewConfig(this.textbookId)
        this.previewUrl = resolveAssetUrl(this.previewConfig.file_url || this.textbook.file_url || '')
        this.currentPage = Number(this.previewConfig.effective_page_start) || 1

        if (isPptFile({
          ...this.textbook,
          ...this.previewConfig,
          file_url: this.previewUrl
        })) {
          this.$router.replace({
            name: 'ppt-preview',
            query: {
              id: Number(this.previewConfig.file_id || this.textbook.file_id) || undefined,
              textbook_id: this.textbookId,
              title: this.textbook.title || this.previewConfig.title || '',
              file_type: this.previewConfig.file_type || this.textbook.file_type || ''
            }
          })
        }
      } catch (error) {
        console.error('加载预览配置失败:', error)
      }
    },

    tocPage(item = {}) {
      return Number(item.page_number || item.page) || 0
    },

    async goToPage(page) {
      const targetPage = Number(page) || 0
      if (!targetPage) return

      if (this.$refs.controlledReader) {
        await this.$refs.controlledReader.requestPage(targetPage)
        return
      }

      if (targetPage >= 1 && targetPage <= this.totalPages) {
        this.currentPage = targetPage
      }
    },

    handleControlledProgress(progress = {}) {
      this.currentPage = Number(progress.current_page) || this.currentPage
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    zoomIn() {
      if (this.zoom < 2) {
        this.zoom += 0.1
      }
    },

    zoomOut() {
      if (this.zoom > 0.5) {
        this.zoom -= 0.1
      }
    },

    resetZoom() {
      this.zoom = 1
    }
  }
}
</script>

<style scoped>
.textbook-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.textbook-page.is-eye-care {
  background: #edf4df;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.textbook-container {
  display: flex;
  height: 100vh;
}

.textbook-sidebar {
  width: 300px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.is-eye-care .textbook-sidebar {
  background: #f8f5e8;
  border-right-color: #d7d6bd;
}

.textbook-header {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.is-eye-care .textbook-header,
.is-eye-care .textbook-info {
  border-bottom-color: #d7d6bd;
}

.textbook-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.btn-close:hover {
  background: #e0e0e0;
}

.textbook-info {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.textbook-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #666;
}

.textbook-toc {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.textbook-toc h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.toc-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.is-eye-care .toc-item {
  background: #eff3dd;
}

.toc-item:hover {
  background: #e6f7ff;
}

.is-eye-care .toc-item:hover {
  background: #ddebc7;
}

.toc-item.is-active {
  background: #1890ff;
  color: #fff;
}

.is-eye-care .toc-item.is-active {
  background: #6f9654;
}

.toc-title {
  flex: 1;
  font-size: 14px;
}

.toc-page {
  font-size: 12px;
  color: #999;
}

.toc-item.is-active .toc-page {
  color: rgba(255, 255, 255, 0.8);
}

.textbook-viewer {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.is-eye-care .textbook-viewer {
  background: #e7efd8;
}

.viewer-toolbar {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.is-eye-care .viewer-toolbar {
  background: #f8f5e8;
  border-bottom-color: #d7d6bd;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-icon {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-icon:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-eye-care.is-active {
  border-color: #6f9654;
  background: #6f9654;
  color: #fff;
}

.btn-eye-care.is-active:hover:not(:disabled) {
  border-color: #5f8346;
  color: #fff;
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator,
.zoom-indicator {
  font-size: 14px;
  color: #666;
  min-width: 80px;
  text-align: center;
}

.viewer-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;
  transform-origin: center center;
  transition: transform 0.2s;
}

.is-eye-care .viewer-content {
  background: #e7efd8;
}

.textbook-iframe {
  width: 100%;
  height: 100%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.is-eye-care .textbook-iframe {
  background: #f7f2d8;
  filter: sepia(0.16) saturate(0.88) brightness(0.94) contrast(0.96);
  box-shadow: 0 8px 28px rgba(80, 96, 62, 0.16);
}

.is-eye-care .viewer-content::after {
  content: '';
  position: sticky;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  min-height: 100%;
  margin-left: -100%;
  pointer-events: none;
  background: rgba(230, 238, 204, 0.16);
  mix-blend-mode: multiply;
}

.no-preview {
  text-align: center;
  padding: 40px;
}

.no-preview p {
  margin-bottom: 20px;
  color: #999;
  font-size: 16px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn--primary {
  background: #1890ff;
  color: #fff;
}

.btn--primary:hover {
  background: #40a9ff;
}
</style>
